import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Row, Container, Form, Spinner, UncontrolledAccordion } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { useTranslation } from "react-i18next";
import APP_ROUTES from "src/routes/routes";
import { useSetRecoilState } from "recoil";
import { navigationPromptState } from "src/recoil/atoms/global";
import { CustomLink } from "src/components/Common/CustomLink";
import { usePatchedNavigate } from "src/Hooks/usePatchedNavigate";
import { FORM_NAME } from "src/pages/TestPackage/detail";
import MainSection from "../components/MainSection";
import FilesHistory from "../components/Files";
import ProtectedComponent from "src/components/Permission";
import useUpdateTestPackage from "src/components/TestPackage/detail/useUpdateTestPackage";
import TooltipWrapper from "src/components/Common/TooltipWrapper";
import { PROCESS_STATUS_NAME } from "src/constant/common";

const TestPackageDetailForm = ({ data, fileHistories, isFetchingFileHistories, getRedirectURL = () => APP_ROUTES.TEST_PACKAGE }) => {
  const { t } = useTranslation("test-package");
  const navigate = usePatchedNavigate();
  const navigationPrompt = useSetRecoilState(navigationPromptState);
  const { submitData: submitTestPackageData, isLoading: isSubmitting } = useUpdateTestPackage({ redirectOnSuccess: getRedirectURL() });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    setValue,
    reset,
  } = useForm({
    defaultValues: {},
  });

  const IsCurrentLinkedInstanceInUse =
    data?.status === PROCESS_STATUS_NAME.RUNNING ||
    data?.status === PROCESS_STATUS_NAME.DEPLOYING ||
    data?.status === PROCESS_STATUS_NAME.DEPLOYED;

  const processSubmit = (submitData) => {
    const { user_input_id, plan_start_date, title, ...restData } = submitData;
    const formData = {
      ...restData,
      id: user_input_id,
      title: title.trim() || "",
      testPackageId: data.id,
      yamcs_package_id: data.yamcs_package_id,
      ...(plan_start_date && { plan_start_date }),
    };
    submitTestPackageData({ data: formData });
  };

  useEffect(() => {
    if (isDirty) {
      navigationPrompt(true);
    }
  }, [isDirty]);

  useEffect(() => {
    if (data) {
      reset({
        [FORM_NAME.MAIN.ID]: data.user_input_id || "",
        [FORM_NAME.MAIN.TITLE]: data.title || "",
        [FORM_NAME.MAIN.PLAN_START_DATE]: data.plan_start_date || "",
      });
    }
  }, [data, reset]);

  const getTitleBreadcrumb = (id, name) => {
    if (!id || !name) return "";
    return (
      <div className="d-flex align-items-center">
        <TooltipWrapper
          id="tooltip-breadcrumb-test-package-id"
          tooltipText={`${id} - ${name}`}
          tooltipClassName="max-width-by-px-300"
          checkOverflow={true}
        >
          <span className="d-block overflow-hidden text-truncate max-width-by-px-300">{`${id} - ${name}`}</span>
        </TooltipWrapper>
      </div>
    );
  };

  return (
    <Container fluid className="project-instance-container">
      <Breadcrumb>
        <BreadcrumbItem>
          <CustomLink to={getRedirectURL()} className="link-primary">
            {t("list.title")}
          </CustomLink>
        </BreadcrumbItem>
        <BreadcrumbItem>{t("detail.title")}</BreadcrumbItem>
        <BreadcrumbItem active>{getTitleBreadcrumb(data?.user_input_id, data?.yamcs_package?.name)}</BreadcrumbItem>
      </Breadcrumb>

      <Row>
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <Form onSubmit={handleSubmit(processSubmit)} className="project-instance-form test-package-form">
                <UncontrolledAccordion defaultOpen={["1"]} stayOpen>
                  <MainSection
                    register={register}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    testPackageData={data}
                    navigationPrompt={navigationPrompt}
                    IsCurrentLinkedInstanceInUse={IsCurrentLinkedInstanceInUse}
                  />
                  <FilesHistory fileHistories={fileHistories} isFetchingFileHistories={isFetchingFileHistories} />
                </UncontrolledAccordion>

                <div className="d-flex justify-content-center gap-2 mt-2">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    className="btn btn-secondary width-by-px-200 height-by-px-38"
                    onClick={() => navigate(getRedirectURL())}
                  >
                    <span>{t("cancel")}</span>
                  </button>
                  <ProtectedComponent id="test_package_update">
                    <button
                      className="btn btn-primary width-by-px-200 height-by-px-38"
                      disabled={isSubmitting || !isDirty || IsCurrentLinkedInstanceInUse}
                      type="submit"
                    >
                      {isSubmitting ? <Spinner size="sm" /> : <span>{t("save")}</span>}
                    </button>
                  </ProtectedComponent>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default TestPackageDetailForm;
